import React from 'react';
import { Box, Typography, Link } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { Section, ResponsiveContainer } from '@lib/common';

export const PrivacyPage: React.FC = () => {
  const { t } = useTranslation();

  const sectionProps = {
    title: t('privacy.title'),
  };

  return (
    <ResponsiveContainer>
      <Section {...sectionProps}>
        <Box mb={2}>
          <Typography align="center">
            Last updated on Jan 1, 2021.
            <br />
            <br />
          </Typography>
          <Typography>
            Armadillo Hotel Group, LLC and its subsidiaries (“AHG”, “we” or “us”) are committed to
            providing you with an efficient and customized Internet experience. This online Privacy
            and Security Notice describes the information we collect through our websites and
            personal devices, how we use that information, and how we protect your personal
            information. This policy also sets forth our practices for obtaining personal
            information from other sources, such as written or verbal communications or information
            collected at a hotel.
            <br />
            <br />
            By submitting your personal information to us, you agree to the transfer to and
            processing of your personal information in the United States in accordance with the
            terms of this Policy.
            <br />
            <br />
          </Typography>
          <Typography variant="h4">WHAT INFORMATION DO WE COLLECT?</Typography>
          <Typography>
            Our websites and mobile applications may receive and store information based on your
            browser settings, which may include your browser type and operating system, the various
            AHG web pages you view, Internet Protocol addresses, unique device identifiers, and
            sites visited before viewing our websites. When applicable, we may compensate other
            websites that include links to our websites. If you have enabled the location settings
            on your device while our mobile app is open, we may also receive your precise
            geo-location information. If you have location settings turned off while our mobile app
            is open, we may receive your general location (country, state, city) information.
            <br />
            <br />
            When you use our reservation system or create an online account, we collect personally
            identifiable information and other information that you supply, such as your name,
            address, telephone number, e-mail address, and credit card details. We may also ask for
            additional information such as a corporate account number or group booking number.
            <br />
            <br />
            Children’s Privacy: Our websites are not intended for children and we do not knowingly
            solicit or collect personal information for persons under the age of 18.
            <br />
            <br />
          </Typography>
          <Typography variant="h4">HOW DO WE USE THE INFORMATION WE COLLECT?</Typography>
          <Typography>
            We do not sell, trade, rent, or release your personal information to anyone outside our
            company, contractors, or affiliates, other than in compliance with this privacy and
            security policy. We and our affiliates use the personal information to administer our
            business activities, provide customer service, personalize your digital experience,
            contact you concerning your stay, and make other products and services available to you.
            We share your information with our credit card issuers and other companies where
            necessary to complete your transaction. We may also use your information to send you
            promotional communications by mail or e-mail, or through our mobile apps, or share your
            information with our business partners.
            <br />
            <br />
          </Typography>
          <Typography variant="h4">RESERVATIONS</Typography>
          <Typography>
            We maintain a centralized reservations system that retains your personally identifiable
            information when you make a reservation via our worldwide accessible websites, mobile
            apps, our call centers, and third-party travel agents and travel websites that connect
            to our reservation system. The personal information collected in the reservation system
            is needed to secure and process your reservation. Your personally identifiable
            information (including credit card number) will be processed primarily in the United
            States to complete your transaction and to facilitate the making of future reservations.
            Only that personal information which is reasonably required to facilitate your
            reservations is collected and shared between AHG and third parties.
            <br />
            <br />
            We will use the information you have provided to send a confirmation and contact you
            regarding your reservation. We may also use your e-mail address to send you
            travel-related messages, stay details, and provide other information about the area and
            hotel; notify you about special offers and promotions; and send you periodic
            satisfaction or market research surveys. Such information may also be sent to you
            through our mobile apps, if you elect to receive notifications and alerts.
            <br />
            <br />
          </Typography>
          <Typography variant="h4">MARKETING, SWEEPSTAKES &amp; CONTESTS</Typography>
          <Typography>
            We may use your personal information to send you via e-mail, telephone, mobile app or
            postal mail promotions, surveys, third party offers, sweepstakes and contests.
            <br />
            <br />
          </Typography>
          <Typography variant="h4">E-FOLIO</Typography>
          <Typography>
            If you participate in the electronic billing e-folio program, your hotel bill will be
            sent to you over the Internet in an unsecure (unencrypted) manner by email or through
            our mobile applications, and could be subject to interception by third parties. If you
            use a corporate credit card, billing information and the details of your hotel stay will
            be shared with the credit card provider who, in turn, will provide it to your employer.
            <br />
            <br />
          </Typography>
          <Typography variant="h4">OTHER USES OF PERSONAL INFORMATION</Typography>
          <Typography>
            To serve you better, AHG may combine information you give us online, at the hotels,
            through the mail, or in any other way. AHG may also combine that information with
            publicly available information and information AHG receives from others or
            cross-references. AHG uses that combined information for the purposes described in this
            privacy statement.
            <br />
            <br />
            To the extent required or permitted by law, we may collect, use and disclose personal
            information in connection with security-related or law enforcement investigations or in
            the course of cooperating with authorities or complying with legal requirements. We may
            also use your information as permitted by law to perform credit checks, report or
            collect debts owed, or protect the rights or property of AHG, our employees, our hotels,
            our customers, this site, or its users.
            <br />
            <br />
          </Typography>
          <Typography variant="h4">
            DO YOU SHARE MY PERSONAL INFORMATION WITH THIRD PARTIES?
          </Typography>
          <Typography>
            AHG will need to transfer your personal information to hotels and third parties to
            secure your reservation or in connection with awarding or redeeming loyalty program
            points. Please note that some hotels may be in countries other than your country of
            residence and may not provide an equivalent level of data protection.
            <br />
            <br />
            AHG distributes personal information about guests to third party service providers such
            as web analytics companies, market researchers and fulfillment houses. We also use such
            third parties to: (i) conduct data processing for AHG, (ii) engage in e-mail and direct
            mail communications on behalf of AHG, (iii) administer sweepstakes and contests, (iv)
            administer guest surveys, (v) compile information for us about the type and frequency of
            guest stays and the use of frequent traveler programs by members, and (vi) compile
            information for us about the use of AHG’s websites and mobile apps. These parties are
            contractually prohibited from using personally identifiable information for any purpose
            other than the purpose that AHG specifies.
            <br />
            <br />
            AHG may also distribute personal information to airline and other companies upon request
            of guests, in order to allow guests to: (i) earn frequent customer miles/points with
            such airline and other companies for stays at hotels, or (ii) redeem points for goods
            and services of such airline and other companies.
            <br />
            <br />
            AHG may partner with other companies to provide co-sponsored or co-branded promotions,
            sweepstakes, contests, products and services and may share your information with these
            companies.
            <br />
            <br />
            Although unlikely, in certain instances, AHG may disclose your personally identifiable
            information when AHG has reason to believe that it is necessary to identify, contact or
            bring legal action against persons or entities who may be causing injury to you, to AHG
            or to others. AHG may also disclose your personally identifiable information when AHG
            believes the law requires it.
            <br />
            <br />
            AHG may disclose your personal information to a third party in the event of any
            reorganization, merger, sale, joint venture, assignment, transfer or other disposition
            of all or any portion of our business, assets or stock (including in connection with any
            bankruptcy or similar proceedings).
            <br />
            <br />
            Special Notification to California Residents: If you reside in California and have
            provided AHG your personally identifiable information, you may request a list from us of
            third parties with whom we shared your personally identifiable information for their own
            direct marketing purposes during the preceding calendar year. Such requests must be
            submitted to us in writing at Armadillo Hotel Group, Attention: Privacy Officer, 9430
            Research Blvd., Building 4 Suite 120, Austin TX 78759. This request may be made no more
            than once per calendar year.
            <br />
            <br />
          </Typography>
          <Typography variant="h4">
            HOW DO I OPT-OUT OF MARKETING COMMUNICATIONS OR OTHERWISE CHANGE MY PRIVACY PREFERENCES?
          </Typography>
          <Typography>
            We respect the right of each visitor to control how his or her personal information is
            used. If you no longer wish to receive promotional communications, please contact us in
            writing at Armadillo Hotel Group, Attention: Privacy Officer, 9430 Research Blvd.,
            Building 4 Suite 120, Austin TX 78759. Please include your name, address, and e-mail
            address and clearly state the nature of your request.
            <br />
            <br />
            AHG makes every reasonable effort to maintain the accuracy of personal information. If
            you become aware that information AHG maintains about you is incorrect or if you would
            like to update your information, please contact us as indicated above.
            <br />
            <br />
          </Typography>
          <Typography variant="h4">HOW DO YOU STORE AND SECURE MY PERSONAL INFORMATION?</Typography>
          <Typography>
            Our reservation system and other databases are maintained in the United States;
            therefore, your personal information will be collected (or transferred) and maintained
            in the United States. Once your information reaches us, we treat it as confidential and
            protect it through a variety of generally accepted industry standards.
            <br />
            <br />
            When you provide us with information through this site, such as your name, address,
            phone number, and credit card information, your information will be encrypted using a
            Transport Layer Security or a Secure Sockets Layer connection. You can tell if your
            information is encrypted by looking for the locked padlock icon on the lower right-hand
            or left-hand corner of your screen and a change in the site address from "http" to
            "https."
            <br />
            <br />
          </Typography>
          <Typography variant="h4">DO YOU USE “COOKIES” AND SIMILAR TECHNOLOGIES?</Typography>
          <Typography>
            AHG’s websites, mobile applications and e-mails may use “cookies” and other similar
            computer code variables. These are small amounts of computer code that interact with
            your computer, mobile device, browser or e-mail. The types of variables AHG uses are
            referred to as “session” variables and “persistent” variables. Session variables are
            temporary and are automatically deleted once you close your internet browser. Persistent
            variables remain on your computer hard drive or mobile device until you delete them or
            are otherwise removed upon expiration. These variables are required for the online
            reservation process so that we can keep your information on multiple pages throughout
            the process. Additionally, AHG uses these variables to remind us of who you are, tailor
            our services to suit your personal interests, track your status in our promotions,
            contests and sweepstakes, and/or analyze your visiting patterns. AHG does not use these
            variables to ascertain any personally identifiable information about you apart from what
            you voluntarily provide to AHG.
            <br />
            <br />
            AHG may also use these variables to form an association between multiple devices such as
            your smartphone, desktop computer and tablet using a unique identifier created on your
            devices. AHG uses the device association to link your devices’ searches on our websites,
            which enables us to provide you with a more convenient and personal digital experience.
            <br />
            <br />
            You can generally set your browser to not accept cookies or to notify you when you are
            sent a cookie, giving you the chance to decide whether or not to accept it. Not
            accepting cookies will adversely affect your ability to perform certain transactions and
            functions on AHG websites.
            <br />
            <br />
            AHG uses pixels or transparent GIF files to help manage its online advertising. These
            files enable AHG and its advertising partners to recognize unique codes on your web
            browser, which in turn, enable AHG to learn which advertisements bring users to its
            websites. The information that AHG collects and shares is anonymous and not personally
            identifiable. It does not contain your name, address, telephone number, or e-mail
            address.
            <br />
            <br />
            Do Not Track Signals: Some web browsers offer a "Do Not Track" ("DNT") signal that is an
            HTTP header field indicating your preference regarding tracking or cross-site user
            tracking. We currently do not recognize DNT signals; however, we do allow you to
            exercise some choice in the information collected by adjusting your browser’s cookie
            settings.
            <br />
            <br />
          </Typography>
          <Typography variant="h4">GOOGLE ANALYTICS</Typography>
          <Typography>
            AHG uses Google Analytics, a web analytics service provided by Google, Inc. (“Google”).
            Google Analytics uses “cookies”, which are text files placed on your computer, to
            identify you as a unique user and help analyze how users use this site. The information
            generated by the cookie about your use of the website (including your IP address, but
            not your name or other identifying information) will be transmitted to and stored by
            Google on servers in the United States. This information is used for the purpose of
            evaluating your use of the website, compiling reports on website activity and providing
            other services relating to website activity and internet usage. The ad formats we
            utilize through Google include Audience Remarketing on Google’s Display and Search
            Network.
            <br />
            <br />
            You may refuse the use of cookies by selecting the appropriate settings on your browser.
            However, please note that if you do this, you may not be able to use the full
            functionality of this website. Furthermore, you can prevent Google’s collection and use
            of data (cookies and IP address) by downloading and installing the browser plug-in
            available for various browsers located on 
            <Link href="https://tools.google.com/dlpage/gaoptout">Google's Tool Portal</Link>.
            <br />
            <br />
            Additional information concerning Google’s data privacy policy and other associated
            topics can be{' '}
            <Link href="https://support.google.com/analytics/answer/6004245">found online</Link>.
            <br />
            <br />
          </Typography>
          <Typography variant="h4">DO YOU USE INTEREST-BASED ADVERTISING?</Typography>
          <Typography>
            AHG partners with third party ad networks to either display advertising on AHG websites
            or to manage its advertising on other sites. AHG’s ad network partners use cookies and
            web beacons to collect non-personally identifiable information about your activities on
            AHG’s websites and other sites to provide you targeted advertising based upon your
            interests. If you wish to not have this information used for the purpose of serving you
            targeted ads, you may opt-out by clicking 
            <Link href="https://preferences-mgr.truste.com">here</Link>. Please note this does not
            opt you out of being served advertising. You will still receive generic ads. Also,
            opting out of ad network cookies does not opt you out of cookies used for device
            association described in this privacy statement.
            <br />
            <br />
            If you would like to know more about cookies, the "
            <Link href="https://www.allaboutcookies.org">All About Cookies</Link>" website provides
            useful information.
            <br />
            <br />
            We also work with third parties that use tracking technologies on our websites in order
            to provide tailored advertisements on our behalf and on behalf of other advertisers
            across the Internet. These companies may collect information about your activity on our
            sites and your interaction with our advertising and other communications, and use this
            information to determine which ads you see on third party websites and applications. If
            you wish for more information about this practice and to understand your options, please
            visit 
            <Link href="https://www.aboutads.info">About Ads</Link>.
            <br />
            <br />
          </Typography>
          <Typography variant="h4">DO YOU USE MY DEVICE'S GEO-LOCATION DATA?</Typography>
          <Typography>
            Our mobile apps may use your device’s Global Positioning System (GPS) or other
            technology to locate a hotel near you and/or to provide you with other relevant
            location-based information and/or services. Your location may be shared with AHG
            business partners to make services or products available to you, such as food delivery
            or local entertainment. While you have AHG’s mobile app open, AHG and its business
            partners may track the device’s precise location unless you have opted out of such
            sharing through your device’s settings. When your device’s location settings are turned
            off but the mobile app is open, AHG and its business partners may collect general
            location information (country, state, city) via IP address or other device information.
            The last location of your mobile device when the mobile app was open may be stored by
            AHG and/or its business partners for marketing purposes. Business partners who have
            access to your precise and/or general location information may use the information only
            in support of AHG’s purposes described in this privacy policy.
            <br />
            <br />
            To the extent any geo-location data is combined with personal information, that
            information will be treated as personal information in accordance with this policy.
            <br />
            <br />
          </Typography>
          <Typography variant="h4">
            WHAT IF I APPLY FOR A CAREER AT AHG ON ARMADILLOHOTEL.COM?
          </Typography>
          <Typography>
            If you apply online for employment at AHG, you may be asked to provide us your name,
            address, e-mail and resume. AHG will use this information solely with respect to your
            job search and to determine your qualifications for a career with AHG.
            <br />
            <br />
          </Typography>
          <Typography variant="h4">
            DOES THIS POLICY COVER OTHER SITES LINKED FROM ARMADILLOHOTEL.COM OR AHG’S MOBILE APPS?
          </Typography>
          <Typography>
            AHG is only responsible for the privacy and security policy and content on this website
            and AHG’s mobile apps. Armadillohotel.com and our mobile apps may redirect to other
            websites and mobile apps. Those websites and mobile apps are not covered by this privacy
            and security policy, and we are not responsible for the privacy practices or the content
            of those other websites and mobile apps.
            <br />
            <br />
          </Typography>
          <Typography variant="h4">
            WHAT ABOUT CHANGES TO THE PRIVACY AND SECURITY POLICY?
          </Typography>
          <Typography>
            By using our website you consent to our collection and use of your personal information
            as described in this policy. AHG reserves the right to modify this privacy and security
            policy and related business practices at any time by posting updated text on this site.
            Any changes to this policy become effective upon posting of the revised policy to this
            site. Use of the site following such changes constitutes your acceptance of the revised
            policy then in effect.
            <br />
            <br />
          </Typography>
          <Typography variant="h4">WHAT IF I HAVE QUESTIONS OR CONCERNS?</Typography>
          <Typography>
            If you have questions about this policy or complaints about how we collect, use,
            disclose, store or protect your personal information as directed by the laws and
            regulations applicable to your region, you can write to us at Armadillo Hotel Group,
            Attention: Privacy Officer, 9430 Research Blvd., Building 4 Suite 120, Austin TX 78759.
            <br />
            <br />
          </Typography>
          <Typography variant="h4">HOW DO YOU RESOLVE COMPLAINTS?</Typography>
          <Typography>
            We will work with you to address complaints, which may include contacting applicable
            authorities as part of the resolution process.
            <br />
            <br />
          </Typography>
          <Typography variant="h4">
            WHAT IF THERE'S A DIFFERENCE BETWEEN VERSIONS OF THE PRIVACY AND SECURITY POLICY?
          </Typography>
          <Typography>
            As a service to our customers, we may make this privacy and security policy available in
            several different languages. In the event of any discrepancies between the various
            translations of the policy, the English version of the policy is the authoritative one.
            <br />
            <br />
          </Typography>
        </Box>
      </Section>
    </ResponsiveContainer>
  );
};
